// web-frontend/src/shared/stores/authStore.js - 웹용 (localStorage persist)
// 🔥🔥🔥 [2025-12-18 수정] 로그인 후 게스트 탄소 데이터 자동 마이그레이션 추가
// v4.5: 게스트 데이터 마이그레이션 통합
// 🔥🔥🔥 [수정] 설문 완료 상태 추가 (surveyCompleted)
// 🔥🔥🔥 [2025-12-19 수정] 에러 핸들링 강화 + template literal 수정 + 인터셉터 제거
// 🔥🔥🔥 [2025-12-24 수정] Template literal 구문 오류 전체 수정
// 🔥🔥🔥 [2026-01-02 핵심 수정] 로그아웃 시 설문 관련 데이터 완전 클리어 - 다른 계정 설문 혼동 버그 수정
// 🔥🔥🔥 [2026-01-07 수정] BUYER/BUSINESS_VISITOR 미승인 상태 권한 제한 추가
// 🔥🔥🔥 [2026-01-07 수정] 템플릿 리터럴 문법 오류 완전 수정
// 🔥🔥🔥 [2026-01-08 핵심 수정] refreshUser 함수 추가 - Admin 승인 시 실시간 권한 갱신
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import apiClient from '../services/apiClient'; // ✅ Default import 사용
import carbonService from '../services/carbonService';
import i18nService from '../services/i18n';
import logger from '../utils/logger';

// ==================== 상수 정의 ====================
const STORAGE_KEY = 'dsk_auth_store';
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// 🔥🔥🔥 [2026-01-02 추가] 설문 관련 localStorage 키 목록
const SURVEY_STORAGE_KEYS = [
  'dsk_survey_answers',
  'dsk_survey_timestamp',
  'dsk_survey_slug',
  'dsk_carbon_prefs',
];

// UUID 검증 함수
const isValidUUID = (uuid) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return typeof uuid === 'string' && uuidRegex.test(uuid);
};

/**
 * 🔥🔥🔥 인증 상태 관리 스토어
 * 
 * 주요 기능:
 * - 사용자 로그인/로그아웃
 * - JWT 토큰 관리 (자동 갱신)
 * - 사용자 프로필 관리
 * - 🔥 로그인 시 게스트 탄소 데이터 자동 마이그레이션
 * - 설문 완료 상태 관리
 * - 다국어(i18n) 연동
 * - 🔥🔥🔥 [2026-01-02] 로그아웃 시 설문 데이터 완전 클리어
 * - 🔥🔥🔥 [2026-01-07] BUYER/BUSINESS_VISITOR 미승인 상태 권한 제한
 * - 🔥🔥🔥 [2026-01-08] refreshUser 함수 - Admin 승인 시 실시간 권한 갱신
 */
const useAuthStore = create(
  persist(
    (set, get) => ({
      // ==================== 상태 ====================
      user: null,
      token: null,
      isAuthenticated: false,
      isGuest: false,
      isLoading: false,
      error: null,
      lastLoginAt: null,
      
      // 게스트 관련 추가 상태
      guestExpiresAt: null,
      guestDaysRemaining: null,
      
      // 🔥🔥🔥 [신규 추가] 설문 완료 상태
      // null: 아직 체크 안함, true: 완료, false: 미완료
      surveyCompleted: null,

      // ==================== 토큰 관리 ====================
      /**
       * 액세스 토큰 저장
       */
      setToken: (token) => {
        if (token) {
          localStorage.setItem(TOKEN_KEY, token);
          apiClient.setToken(token);
        } else {
          localStorage.removeItem(TOKEN_KEY);
          apiClient.clearToken();
        }
        set({ token });
      },

      /**
       * 리프레시 토큰 저장
       */
      setRefreshToken: (token) => {
        if (token) {
          localStorage.setItem(REFRESH_TOKEN_KEY, token);
        } else {
          localStorage.removeItem(REFRESH_TOKEN_KEY);
        }
      },

      /**
       * 저장된 토큰 가져오기
       */
      getToken: () => localStorage.getItem(TOKEN_KEY),
      getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),

      // ==================== 초기화 함수들 ====================
      /**
       * 🔥 완전 초기화 함수
       */
      resetToInitialState: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isGuest: false,
          guestExpiresAt: null,
          guestDaysRemaining: null,
          error: null,
          isLoading: false,
          lastLoginAt: null,
          surveyCompleted: null, // 🔥 설문 상태도 초기화
        });
      },

      /**
       * 전체 상태 초기화
       */
      reset: () => {
        get().setToken(null);
        get().setRefreshToken(null);
        
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isGuest: false,
          isLoading: false,
          error: null,
          lastLoginAt: null,
          guestExpiresAt: null,
          guestDaysRemaining: null,
          surveyCompleted: null,
        });
      },

      // ==================== 설문 상태 관리 ====================
      /**
       * 🔥🔥🔥 [신규 추가] 설문 완료 상태 설정
       */
      setSurveyCompleted: (completed) => {
        logger.debug('📋 [AuthStore] 설문 완료 상태 설정:', completed);
        set({ surveyCompleted: completed });
      },

      /**
       * 🔥🔥🔥 [신규 추가] 설문 상태 초기화 (로그인 시 호출)
       */
      resetSurveyStatus: () => {
        logger.debug('📋 [AuthStore] 설문 상태 초기화');
        set({ surveyCompleted: null });
      },

      /**
       * 🔥🔥🔥 [신규 추가] 설문 완료 여부 체크
       */
      isSurveyCompleted: () => {
        const { surveyCompleted } = get();
        return surveyCompleted === true;
      },

      // ==================== 토큰 검증 ====================
      /**
       * 🔥 토큰 검증 및 자동 로그아웃
       */
      verifyToken: async () => {
        try {
          logger.debug('🔍 authStore: 토큰 검증 시작...');
          const { token, isAuthenticated } = get();
          
          if (!isAuthenticated || !token) {
            logger.debug('ℹ️ 인증되지 않은 상태');
            return { valid: false };
          }
          
          // JWT 토큰 만료 확인
          const { validateStoredToken } = await import('../utils/storage');
          const validation = await validateStoredToken();
          
          if (!validation.valid) {
            logger.warn('⚠️ 토큰이 만료되었거나 유효하지 않음 - 자동 로그아웃');
            // 자동 로그아웃
            const logout = get().logout;
            await logout();
            return { valid: false, reason: '토큰 만료' };
          }
          
          logger.debug('✅ 토큰 검증 성공');
          return { valid: true };
          
        } catch (error) {
          logger.error('❌ 토큰 검증 실패:', error);
          // 에러 발생 시 안전하게 로그아웃
          const logout = get().logout;
          await logout();
          return { valid: false, reason: error.message };
        }
      },

      // ==================== 로그인 ====================
      /**
       * 🔥🔥🔥 로그인 - 게스트 데이터 마이그레이션 통합
       */
      login: async (credentials) => {
        set({ isLoading: true, error: null });
        
        try {
          logger.debug('[authStore] 로그인 시도:', credentials.email || credentials.username);
          
          const response = await apiClient.post('/auth/login', credentials);
          
          // 🔥 응답 데이터 유효성 검사
          if (!response) {
            throw new Error('서버로부터 응답을 받지 못했습니다.');
          }
          
          const { access_token, refresh_token, user } = response;
          
          // 토큰 저장
          get().setToken(access_token);
          get().setRefreshToken(refresh_token);
          
          // 🔥 언어 설정 동기화 (로컬 설정 우선)
          if (user.preferred_language) {
            const localLang = typeof window !== 'undefined' ? localStorage.getItem('dsk_language') : null;
            const finalLang = localLang || user.preferred_language;
            logger.debug('🌐 Web: 언어 적용 (로컬:', localLang, '서버:', user.preferred_language, '→', finalLang, ')');
            i18nService.changeLanguage(finalLang);
            if (typeof window !== 'undefined') {
              localStorage.setItem('dsk_language', finalLang);
            }
          }
          
          // 게스트 정보 처리
          const isGuestUser = user.is_guest || user.user_type === 'GUEST' || false;
          let guestExpiresAt = null;
          let guestDaysRemaining = null;
          
          if (isGuestUser && user.guest_expires_at) {
            guestExpiresAt = user.guest_expires_at;
            guestDaysRemaining = user.days_until_guest_expiration || 0;
          }
          
          // 🔥🔥🔥 [2026-01-02 추가] 로그인 시 이전 사용자의 설문 캐시 클리어
          get().clearSurveyCache();
          
          // 상태 업데이트
          set({
            user,
            isAuthenticated: true,
            isGuest: isGuestUser,
            guestExpiresAt,
            guestDaysRemaining,
            isLoading: false,
            error: null,
            lastLoginAt: new Date().toISOString(),
            surveyCompleted: null, // 🔥 로그인 시 설문 상태 초기화
          });
          
          logger.debug('[authStore] ✅ 로그인 성공:', user.email);
          
          // 🔥🔥🔥 [핵심] 로그인 성공 후 게스트 탄소 데이터 마이그레이션
          // 비동기로 실행하여 로그인 UX에 영향 없도록 함
          setTimeout(async () => {
            try {
              const migrationResult = await carbonService.migrateGuestData();
              
              if (migrationResult.migrated_count > 0) {
                logger.debug('[authStore] 🌱 게스트 탄소 데이터 마이그레이션 완료:', migrationResult.migrated_count, '개');
                
                // 사용자 통계 업데이트
                const currentUser = get().user;
                if (currentUser) {
                  set({
                    user: {
                      ...currentUser,
                      carbon_points: (currentUser.carbon_points || 0) + (migrationResult.total_points_added || 0),
                      total_carbon_saved: (currentUser.total_carbon_saved || 0) + (migrationResult.total_carbon_saved_added || 0)
                    }
                  });
                }
              }
            } catch (migrationError) {
              // 마이그레이션 실패해도 로그인은 성공으로 처리
              logger.warn('[authStore] 게스트 데이터 마이그레이션 실패 (무시됨):', migrationError);
            }
          }, 500); // 500ms 딜레이 후 마이그레이션 실행
          
          return { success: true, user };
          
        } catch (error) {
          logger.error('[authStore] ❌ 로그인 실패:', error);
          
          // 🔥🔥🔥 [핵심 수정] 안전한 에러 메시지 추출
          let errorMessage = '로그인에 실패했습니다';
          
          if (error.response?.data?.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.data?.detail) {
            errorMessage = error.data.detail;
          } else if (error.data?.message) {
            errorMessage = error.data.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          set({
            isLoading: false,
            error: errorMessage
          });
          
          return { success: false, error: errorMessage };
        }
      },

      // ==================== 소셜 로그인 ====================
      /**
       * 소셜 로그인 콜백 처리 (Google, Kakao 등)
       */
      handleSocialLoginCallback: async (provider, code, state) => {
        set({ isLoading: true, error: null });
        
        try {
          logger.debug('[authStore]', provider, '소셜 로그인 콜백 처리');
          
          const response = await apiClient.post('/auth/' + provider + '/callback', {
            code,
            state
          });
          
          // 🔥 응답 데이터 유효성 검사
          if (!response) {
            throw new Error('서버로부터 응답을 받지 못했습니다.');
          }
          
          const { access_token, refresh_token, user } = response;
          
          // 토큰 저장
          get().setToken(access_token);
          get().setRefreshToken(refresh_token);
          
          // 🔥 언어 설정 동기화 (로컬 설정 우선)
          if (user.preferred_language) {
            const localLang = typeof window !== 'undefined' ? localStorage.getItem('dsk_language') : null;
            const finalLang = localLang || user.preferred_language;
            i18nService.changeLanguage(finalLang);
            if (typeof window !== 'undefined') {
              localStorage.setItem('dsk_language', finalLang);
            }
          }
          
          // 게스트 정보 처리
          const isGuestUser = user.is_guest || user.user_type === 'GUEST' || false;
          
          // 🔥🔥🔥 [2026-01-02 추가] 소셜 로그인 시에도 설문 캐시 클리어
          get().clearSurveyCache();
          
          // 상태 업데이트
          set({
            user,
            isAuthenticated: true,
            isGuest: isGuestUser,
            isLoading: false,
            error: null,
            lastLoginAt: new Date().toISOString(),
            surveyCompleted: null,
          });
          
          logger.debug('[authStore] ✅', provider, '소셜 로그인 성공:', user.email);
          
          // 🔥🔥🔥 [핵심] 소셜 로그인 후에도 게스트 데이터 마이그레이션
          setTimeout(async () => {
            try {
              const migrationResult = await carbonService.migrateGuestData();
              if (migrationResult.migrated_count > 0) {
                logger.debug('[authStore] 🌱 소셜 로그인 후 게스트 데이터 마이그레이션 완료:', migrationResult.migrated_count, '개');
                
                const currentUser = get().user;
                if (currentUser) {
                  set({
                    user: {
                      ...currentUser,
                      carbon_points: (currentUser.carbon_points || 0) + (migrationResult.total_points_added || 0),
                      total_carbon_saved: (currentUser.total_carbon_saved || 0) + (migrationResult.total_carbon_saved_added || 0)
                    }
                  });
                }
              }
            } catch (migrationError) {
              logger.warn('[authStore] 소셜 로그인 마이그레이션 실패 (무시됨):', migrationError);
            }
          }, 500);
          
          return { success: true, user };
          
        } catch (error) {
          logger.error('[authStore] ❌', provider, '소셜 로그인 실패:', error);
          
          // 🔥🔥🔥 [핵심 수정] 안전한 에러 메시지 추출
          let errorMessage = provider + ' 로그인에 실패했습니다';
          
          if (error.response?.data?.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.data?.detail) {
            errorMessage = error.data.detail;
          } else if (error.data?.message) {
            errorMessage = error.data.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          set({
            isLoading: false,
            error: errorMessage
          });
          
          return { success: false, error: errorMessage };
        }
      },

      // ==================== 회원가입 ====================
      /**
       * 회원가입
       */
      register: async (userData) => {
        set({ isLoading: true, error: null });
        
        try {
          logger.debug('[authStore] 회원가입 시도:', userData.email);
          
          const response = await apiClient.post('/auth/register', userData);
          
          // 🔥 응답 데이터 유효성 검사
          if (!response) {
            throw new Error('서버로부터 응답을 받지 못했습니다.');
          }
          
          const { access_token, refresh_token, user } = response;
          
          // 토큰 저장 (회원가입 후 자동 로그인)
          get().setToken(access_token);
          get().setRefreshToken(refresh_token);
          
          // 🔥 언어 설정 동기화 (로컬 설정 우선)
          if (user.preferred_language) {
            const localLang = typeof window !== 'undefined' ? localStorage.getItem('dsk_language') : null;
            const finalLang = localLang || user.preferred_language;
            i18nService.changeLanguage(finalLang);
            if (typeof window !== 'undefined') {
              localStorage.setItem('dsk_language', finalLang);
            }
          }
          
          // 🔥🔥🔥 [2026-01-02 추가] 회원가입 시에도 설문 캐시 클리어
          get().clearSurveyCache();
          
          // 상태 업데이트
          set({
            user,
            isAuthenticated: true,
            isGuest: false,
            isLoading: false,
            error: null,
            lastLoginAt: new Date().toISOString(),
            surveyCompleted: null,
          });
          
          logger.debug('[authStore] ✅ 회원가입 성공:', user.email);
          
          // 🔥🔥🔥 회원가입 후에도 게스트 데이터 마이그레이션 (설문 등 이미 진행한 경우)
          setTimeout(async () => {
            try {
              const migrationResult = await carbonService.migrateGuestData();
              if (migrationResult.migrated_count > 0) {
                logger.debug('[authStore] 🌱 회원가입 후 게스트 데이터 마이그레이션 완료:', migrationResult.migrated_count, '개');
                
                const currentUser = get().user;
                if (currentUser) {
                  set({
                    user: {
                      ...currentUser,
                      carbon_points: (currentUser.carbon_points || 0) + (migrationResult.total_points_added || 0),
                      total_carbon_saved: (currentUser.total_carbon_saved || 0) + (migrationResult.total_carbon_saved_added || 0)
                    }
                  });
                }
              }
            } catch (migrationError) {
              logger.warn('[authStore] 회원가입 마이그레이션 실패 (무시됨):', migrationError);
            }
          }, 500);
          
          return { success: true, user };
          
        } catch (error) {
          logger.error('[authStore] ❌ 회원가입 실패:', error);
          
          // 🔥🔥🔥 [핵심 수정] 안전한 에러 메시지 추출
          let errorMessage = '회원가입에 실패했습니다';
          
          if (error.response?.data?.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.data?.detail) {
            errorMessage = error.data.detail;
          } else if (error.data?.message) {
            errorMessage = error.data.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          set({
            isLoading: false,
            error: errorMessage
          });
          
          return { success: false, error: errorMessage };
        }
      },

      // ==================== 게스트 로그인 ====================
      /**
       * 게스트 로그인
       */
      guestLogin: async () => {
        set({ isLoading: true, error: null });
        
        try {
          logger.debug('👤 게스트 로그인 시도');
          
          set({
            isLoading: false,
            isGuest: true,
            isAuthenticated: false,
            surveyCompleted: null, // 🔥 게스트 로그인 시 설문 상태 초기화
          });
          
          return { success: true, isGuest: true };
          
        } catch (error) {
          set({
            error: error.message,
            isLoading: false,
          });
          return { success: false, error: error.message };
        }
      },

      // ==================== 🔥🔥🔥 [2026-01-02 신규] 설문 캐시 클리어 ====================
      /**
       * 설문 관련 localStorage/sessionStorage 데이터 클리어
       * 
       * 🔥🔥🔥 [핵심] 사용자 전환 시 이전 사용자의 설문 데이터가 남아있지 않도록 완전 클리어
       */
      clearSurveyCache: () => {
        logger.debug('🧹 [AuthStore] 설문 캐시 클리어 시작...');
        
        try {
          // localStorage 설문 관련 키 클리어
          SURVEY_STORAGE_KEYS.forEach(key => {
            try {
              localStorage.removeItem(key);
              logger.debug('   ✓ localStorage.' + key + ' 삭제');
            } catch (e) {
              logger.warn('   ⚠️ ' + key + ' 삭제 실패:', e);
            }
          });
          
          // sessionStorage 설문 관련 키 클리어
          try {
            sessionStorage.removeItem('lastSurveyCheckTime');
            sessionStorage.removeItem('pendingSurveys');
            logger.debug('   ✓ sessionStorage 설문 캐시 삭제');
          } catch (e) {
            logger.warn('   ⚠️ sessionStorage 클리어 실패:', e);
          }
          
          logger.debug('✅ [AuthStore] 설문 캐시 클리어 완료');
        } catch (error) {
          logger.error('❌ [AuthStore] 설문 캐시 클리어 실패:', error);
        }
      },

      // ==================== 로그아웃 ====================
      /**
       * ✅ 로그아웃 - 언어 설정 보존하도록 수정
       * 🔥🔥🔥 [2026-01-02 수정] 설문 관련 데이터 완전 클리어 추가
       */
      logout: async () => {
        try {
          logger.debug('[authStore] 로그아웃 시도');
          
          // 서버에 로그아웃 알림 (옵션)
          try {
            await apiClient.post('/auth/logout');
          } catch (e) {
            // 서버 로그아웃 실패해도 클라이언트 로그아웃은 진행
            logger.warn('[authStore] 서버 로그아웃 알림 실패 (무시됨)');
          }
          
          // 토큰 제거
          get().setToken(null);
          get().setRefreshToken(null);
          
          // 🔥🔥🔥 [2026-01-02 핵심 추가] 설문 캐시 클리어
          get().clearSurveyCache();
          
          // 1. 먼저 상태 초기화 (동기적으로)
          const resetToInitialState = get().resetToInitialState;
          resetToInitialState();
          logger.debug('✅ zustand 상태 초기화 완료');
          
          // 2. localStorage 클리어 (🔥 dsk_language는 제외!)
          const keysToRemove = [
            'auth-storage',
            'auth_token',
            'refresh_token',
            'user_data',
            'user_id',
            'device_token',
            STORAGE_KEY,
            TOKEN_KEY,
            REFRESH_TOKEN_KEY,
            // 🔥🔥🔥 [2026-01-02 추가] 설문 관련 키도 포함
            ...SURVEY_STORAGE_KEYS,
            // 🔥 [2026-05-30] SPC 브릿지 컨텍스트 — 로그아웃 시 정리해 세션/계정 간 stale 라우팅 방지
            'dsk_spc_bridge_context',
            // 🔥 'dsk_language'는 여기 포함하지 않습니다.
            // 로그아웃 후에도 사용자가 마지막으로 선택한 언어를 유지합니다.
          ];
          
          keysToRemove.forEach(key => {
            try {
              localStorage.removeItem(key);
            } catch (e) {
              logger.warn('⚠️ ' + key + ' 삭제 실패 (무시):', e);
            }
          });
          
          // 🔥 세션 스토리지 설문 체크 상태 클리어
          try {
            sessionStorage.removeItem('lastSurveyCheckTime');
            sessionStorage.removeItem('pendingSurveys');
          } catch (e) {
            logger.warn('⚠️ sessionStorage 클리어 실패 (무시):', e);
          }
          
          logger.debug('✅ localStorage 클리어 완료');
          logger.debug('[authStore] ✅ 로그아웃 완료 (언어 설정 유지, 설문 캐시 클리어됨)');
          
          return { success: true };
          
        } catch (error) {
          logger.error('[authStore] ❌ 로그아웃 실패:', error);
          return { success: false, error: error.message };
        }
      },

      // ==================== 토큰 갱신 ====================
      /**
       * 액세스 토큰 갱신
       */
      refreshAccessToken: async () => {
        try {
          const refreshToken = get().getRefreshToken();
          
          if (!refreshToken) {
            throw new Error('리프레시 토큰이 없습니다');
          }
          
          logger.debug('[authStore] 토큰 갱신 시도');
          
          const response = await apiClient.post('/auth/refresh', {
            refresh_token: refreshToken
          });
          
          // 🔥 응답 데이터 유효성 검사
          if (!response) {
            throw new Error('토큰 갱신 응답이 없습니다');
          }
          
          const { access_token, refresh_token: newRefreshToken } = response;
          
          // 새 토큰 저장
          get().setToken(access_token);
          if (newRefreshToken) {
            get().setRefreshToken(newRefreshToken);
          }
          
          logger.debug('[authStore] ✅ 토큰 갱신 성공');
          
          return { success: true };
          
        } catch (error) {
          logger.error('[authStore] ❌ 토큰 갱신 실패:', error);
          
          // 갱신 실패 시 로그아웃
          get().logout();
          
          return { success: false, error: error.message };
        }
      },

      // ==================== 사용자 정보 ====================
      /**
       * 현재 사용자 정보 조회
       */
      fetchCurrentUser: async () => {
        try {
          const token = get().getToken();
          
          if (!token) {
            return { success: false, error: '토큰이 없습니다' };
          }
          
          logger.debug('[authStore] 사용자 정보 조회');
          
          const response = await apiClient.get('/users/me');
          
          // 🔥 응답 데이터 유효성 검사
          if (!response) {
            throw new Error('사용자 정보 응답이 없습니다');
          }
          
          const user = { ...get().user, ...response };
          
          // 🔥 언어 설정 동기화 (로컬 설정 우선)
          if (user.preferred_language) {
            const localLang = typeof window !== 'undefined' ? localStorage.getItem('dsk_language') : null;
            const finalLang = localLang || user.preferred_language;
            i18nService.changeLanguage(finalLang);
            if (typeof window !== 'undefined') {
              localStorage.setItem('dsk_language', finalLang);
            }
          }
          
          // 게스트 정보 처리
          const isGuestUser = user.is_guest || user.user_type === 'GUEST' || false;
          
          set({ 
            user, 
            isAuthenticated: true,
            isGuest: isGuestUser,
          });
          
          logger.debug('[authStore] ✅ 사용자 정보 조회 성공:', user.email);
          
          return { success: true, user };
          
        } catch (error) {
          logger.error('[authStore] ❌ 사용자 정보 조회 실패:', error);
          
          // 401 에러면 로그아웃
          if (error.response?.status === 401 || error.status === 401) {
            get().logout();
          }
          
          return { success: false, error: error.message };
        }
      },

      // ==================== 🔥🔥🔥 [2026-01-08 핵심 신규] 사용자 정보 실시간 갱신 ====================
      /**
       * 🔥🔥🔥 [핵심] refreshUser - Admin 승인 후 실시간 권한 갱신
       * 
       * fetchCurrentUser와 유사하지만, 다음이 다릅니다:
       * - 이전 user 상태와 비교하여 변경사항 로깅
       * - is_verified 변경 시 특별 처리
       * - 캐시 무효화 및 권한 즉시 반영
       * 
       * 사용 시점:
       * - Admin에서 승인 알림 수신 시 (MainLayout.jsx)
       * - 탭 재활성화 시 (Visibility API)
       * - 주기적 폴링 시
       */
      refreshUser: async () => {
        try {
          const token = get().getToken();
          
          if (!token) {
            logger.warn('[authStore] refreshUser: 토큰 없음');
            return { success: false, error: '토큰이 없습니다' };
          }
          
          const previousUser = get().user;
          
          logger.debug('[authStore] 🔄 사용자 정보 새로고침 시작...');
          logger.debug('[authStore] 이전 상태:', {
            user_type: previousUser?.user_type,
            is_verified: previousUser?.is_verified
          });
          
          const response = await apiClient.get('/users/me');
          
          // 🔥 응답 데이터 유효성 검사
          if (!response) {
            throw new Error('사용자 정보 응답이 없습니다');
          }
          
          const user = { ...get().user, ...response };
          
          // 🔥 언어 설정 동기화 (로컬 설정 우선)
          if (user.preferred_language) {
            const localLang = typeof window !== 'undefined' ? localStorage.getItem('dsk_language') : null;
            const finalLang = localLang || user.preferred_language;
            i18nService.changeLanguage(finalLang);
            if (typeof window !== 'undefined') {
              localStorage.setItem('dsk_language', finalLang);
            }
          }
          
          // 게스트 정보 처리
          const isGuestUser = user.is_guest || user.user_type === 'GUEST' || false;
          let guestExpiresAt = null;
          let guestDaysRemaining = null;
          
          if (isGuestUser && user.guest_expires_at) {
            guestExpiresAt = user.guest_expires_at;
            guestDaysRemaining = user.days_until_guest_expiration || 0;
          }
          
          // 🔥🔥🔥 [핵심] 승인 상태 변경 감지
          const wasVerified = previousUser?.is_verified;
          const isNowVerified = user.is_verified;
          
          if (!wasVerified && isNowVerified) {
            logger.debug('🎉 [authStore] 승인 상태 변경 감지: 미승인 → 승인됨!');
            logger.debug('   user_type:', user.user_type);
            logger.debug('   is_verified:', user.is_verified);
          }
          
          // 상태 업데이트
          set({ 
            user, 
            isAuthenticated: true,
            isGuest: isGuestUser,
            guestExpiresAt,
            guestDaysRemaining,
          });
          
          logger.debug('[authStore] ✅ 사용자 정보 새로고침 완료:', {
            id: user.id,
            email: user.email,
            user_type: user.user_type,
            is_verified: user.is_verified,
            변경됨: wasVerified !== isNowVerified
          });
          
          return { success: true, user };
          
        } catch (error) {
          logger.error('[authStore] ❌ 사용자 정보 새로고침 실패:', error);
          
          // 401 에러면 로그아웃
          if (error.response?.status === 401 || error.status === 401) {
            get().logout();
          }
          
          return { success: false, error: error.message };
        }
      },

      /**
       * 사용자 프로필 업데이트
       */
      updateProfile: async (profileData) => {
        set({ isLoading: true, error: null });
        
        try {
          logger.debug('[authStore] 프로필 업데이트 시도');
          
          const response = await apiClient.patch('/users/me', profileData);
          
          // 🔥 응답 데이터 유효성 검사
          if (!response) {
            throw new Error('프로필 업데이트 응답이 없습니다');
          }
          
          const updatedUser = response;
          
          // 🔥 언어 설정 변경 감지 및 적용
          if (updatedUser.preferred_language) {
            i18nService.changeLanguage(updatedUser.preferred_language);
            if (typeof window !== 'undefined') {
              localStorage.setItem('dsk_language', updatedUser.preferred_language);
            }
          }
          
          set({
            user: { ...get().user, ...updatedUser },
            isLoading: false
          });
          
          logger.debug('[authStore] ✅ 프로필 업데이트 성공');
          
          return { success: true, user: updatedUser };
          
        } catch (error) {
          logger.error('[authStore] ❌ 프로필 업데이트 실패:', error);
          
          // 🔥🔥🔥 [핵심 수정] 안전한 에러 메시지 추출
          let errorMessage = '프로필 업데이트에 실패했습니다';
          
          if (error.response?.data?.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.data?.detail) {
            errorMessage = error.data.detail;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          set({
            isLoading: false,
            error: errorMessage
          });
          
          return { success: false, error: errorMessage };
        }
      },

      // ==================== 사용자 상태 업데이트 ====================
      /**
       * 🔥 [수정] updateUser: 언어 변경 시 LocalStorage 즉시 반영 추가
       */
      updateUser: (userData) => {
        set(state => {
          const currentUser = state.user || {};
          
          // 🔥 언어 설정 변경 감지 및 적용
          if (userData.preferred_language && userData.preferred_language !== currentUser.preferred_language) {
            logger.debug('🌐 Web: 프로필 업데이트로 언어 변경:', userData.preferred_language);
            // 1. i18n 서비스 변경
            i18nService.changeLanguage(userData.preferred_language);
            // 2. [추가] 새로고침 대비 LocalStorage 강제 저장
            if (typeof window !== 'undefined') {
              localStorage.setItem('dsk_language', userData.preferred_language);
            }
          }
          
          let userId = userData.id || currentUser.id;
          
          // UUID 검증 (기존 유저가 없는 경우는 검증 스킵)
          if (userId && !isValidUUID(userId)) {
            logger.warn('⚠️ updateUser: 유효하지 않은 UUID:', userId);
            return state;
          }
          
          // 게스트 정보 처리 - 기존 상태 유지하면서 새 값이 있으면 덮어쓰기
          const isGuestUser = (userData.is_guest !== undefined) 
            ? userData.is_guest 
            : (userData.user_type === 'GUEST' || state.isGuest || false);
          
          let guestExpiresAt = userData.guest_expires_at || state.guestExpiresAt;
          let guestDaysRemaining = userData.days_until_guest_expiration !== undefined 
            ? userData.days_until_guest_expiration 
            : state.guestDaysRemaining;
          
          // 게스트가 아니면 게스트 관련 정보 초기화
          if (!isGuestUser) {
            guestExpiresAt = null;
            guestDaysRemaining = null;
          }
          
          // 🔥 새로운 유저 객체 생성 (Deep Merge 효과)
          // 기존 정보를 깔고, 새 정보로 덮어쓰기 (profile_image 등 포함)
          const newUser = {
            ...currentUser,  // 기존 정보 유지
            ...userData,     // 새 정보 덮어쓰기
            id: userId       // ID는 명시적으로 설정
          };
          
          logger.debug('🔄 [AuthStore] User updated:', {
            id: newUser.id,
            profile_image: newUser.profile_image,
            name: newUser.name
          });
          
          return {
            user: newUser,
            isGuest: isGuestUser,
            guestExpiresAt,
            guestDaysRemaining,
          };
        });
      },

      /**
       * 🔥 [수정] setUser: 로그인 직후 언어 설정 강화
       */
      setUser: (user) => {
        if (!user || !user.id || !isValidUUID(user.id)) {
          logger.warn('⚠️ setUser: 유효하지 않은 사용자 데이터:', user);
          return;
        }
        
        logger.debug('✅ setUser: 사용자 설정됨', user.id);
        
        // 🔥 언어 설정 동기화 (로컬 설정 우선)
        if (user.preferred_language) {
          const localLang = typeof window !== 'undefined' ? localStorage.getItem('dsk_language') : null;
          const finalLang = localLang || user.preferred_language;
          logger.debug('🌐 Web: 언어 적용 (로컬:', localLang, '서버:', user.preferred_language, '→', finalLang, ')');
          i18nService.changeLanguage(finalLang);
          if (typeof window !== 'undefined') {
            localStorage.setItem('dsk_language', finalLang);
          }
        }
        
        const isGuestUser = user.is_guest || user.user_type === 'GUEST' || false;
        let guestExpiresAt = null;
        let guestDaysRemaining = null;
        
        if (isGuestUser && user.guest_expires_at) {
          guestExpiresAt = user.guest_expires_at;
          guestDaysRemaining = user.days_until_guest_expiration || 0;
        }
        
        set({
          user: user,
          isAuthenticated: true,
          isGuest: isGuestUser,
          guestExpiresAt,
          guestDaysRemaining,
          error: null,
          surveyCompleted: null, // 🔥 새 로그인 시 설문 상태 초기화 (다시 체크하도록)
        });
      },

      // ==================== 탄소 통계 업데이트 ====================
      /**
       * 🔥🔥🔥 탄소 통계 업데이트 (마이그레이션 후 호출)
       */
      updateCarbonStats: (stats) => {
        const currentUser = get().user;
        
        if (currentUser) {
          set({
            user: {
              ...currentUser,
              carbon_points: stats.carbon_points ?? currentUser.carbon_points,
              total_carbon_saved: stats.total_carbon_saved ?? currentUser.total_carbon_saved
            }
          });
          
          logger.debug('[authStore] 🌱 탄소 통계 업데이트:', stats);
        }
      },

      // ==================== 초기화 ====================
      /**
       * 앱 시작 시 인증 상태 초기화
       */
      initializeAuth: async () => {
        const token = get().getToken();
        
        if (!token) {
          logger.debug('[authStore] 저장된 토큰 없음');
          return { success: false };
        }
        
        // 토큰을 API 클라이언트에 설정
        apiClient.setToken(token);
        
        // 사용자 정보 조회
        return get().fetchCurrentUser();
      },

      /**
       * 에러 초기화
       */
      clearError: () => set({ error: null }),

      // ==================== 유틸리티 함수들 ====================
      getCurrentUserId: () => {
        const { user } = get();
        if (!user?.id || !isValidUUID(user.id)) {
          logger.warn('⚠️ getCurrentUserId: 유효하지 않은 UUID');
          return null;
        }
        return user.id;
      },

      isGuestUser: () => {
        const { user, isGuest } = get();
        return isGuest || user?.is_guest === true || user?.user_type === 'GUEST';
      },

      isGuestExpired: () => {
        const { guestExpiresAt, isGuest } = get();
        if (!isGuest || !guestExpiresAt) return false;
        
        try {
          const expirationDate = new Date(guestExpiresAt);
          return new Date() > expirationDate;
        } catch (error) {
          logger.error('게스트 만료 확인 실패:', error);
          return false;
        }
      },

      getGuestDaysRemaining: () => {
        const { guestDaysRemaining, isGuest } = get();
        if (!isGuest) return null;
        return guestDaysRemaining;
      },

      convertToMember: (userData) => {
        set(state => ({
          user: {
            ...state.user,
            ...userData,
            is_guest: false,
            user_type: userData.user_type || 'VISITOR',
          },
          isGuest: false,
          guestExpiresAt: null,
          guestDaysRemaining: null,
          surveyCompleted: null, // 🔥 회원 전환 시 설문 상태 초기화
        }));
      },

      // ==================== 권한 체크 ====================
      /**
       * 🔥🔥🔥 [2026-01-07 수정] 기능 접근 권한 체크 - BUYER/BUSINESS_VISITOR 미승인 상태 추가
       */
      canAccessFeature: (feature) => {
        const { user, isGuest } = get();
        
        // 1. GUEST 제한 (기존 로직)
        if (isGuest) {
          const guestRestrictions = {
            'meeting_request': false,
            'chat': false,
            'carbon_points': false,
            'donation': false,
            'matching': false,
            'companies': true,
            'events': true,
            'ai_curator': true,
          };
          return guestRestrictions[feature] !== false;
        }
        
        // 2. VISITOR 제한 (기존 로직)
        if (user?.user_type === 'VISITOR') {
          if (feature === 'matching') return false;
          if (feature === 'chat') {
            return user.has_chat_permission === true;
          }
          if (feature === 'meeting_request') {
            return user.has_meeting_permission === true;
          }
        }
        
        // 3. 🔥🔥🔥 [신규] BUYER 미승인 상태 제한
        if (user?.user_type === 'BUYER') {
          // 미승인 BUYER는 VISITOR와 동일한 권한
          if (!user.is_verified) {
            if (feature === 'matching') return false;
            if (feature === 'chat') return false;
            if (feature === 'meeting_request') return false;
          }
          // 승인된 BUYER는 모든 기능 허용
          return true;
        }
        
        // 4. 🔥🔥🔥 [신규] BUSINESS_VISITOR 미승인 상태 제한
        if (user?.user_type === 'BUSINESS_VISITOR') {
          // 미승인 BUSINESS_VISITOR는 VISITOR와 동일한 권한
          if (!user.is_verified) {
            if (feature === 'matching') return false;
            if (feature === 'chat') return false;
            if (feature === 'meeting_request') return false;
          }
          // 승인된 BUSINESS_VISITOR는 matching 제외 모두 허용
          if (feature === 'matching') return false;
          return true;
        }
        
        // 5. INVESTOR 제한 (기존 로직)
        if (user?.user_type === 'INVESTOR') {
          if (feature === 'matching') return false;
          return true;
        }
        
        // 그 외 유저(COMPANY 등)는 모두 허용
        return true;
      },

      // 🔥 [기존] VISITOR 권한 상태 확인 헬퍼 함수들
      hasVisitorChatPermission: () => {
        const { user } = get();
        if (user?.user_type !== 'VISITOR') return true; // VISITOR가 아니면 true
        return user.has_chat_permission || false;
      },

      hasVisitorMeetingPermission: () => {
        const { user } = get();
        if (user?.user_type !== 'VISITOR') return true; // VISITOR가 아니면 true
        return user.has_meeting_permission || false;
      },

      // 🔥 [기존] 사용자 타입별 체크 헬퍼 함수들
      isVisitor: () => {
        const { user } = get();
        return user?.user_type === 'VISITOR';
      },

      isInvestor: () => {
        const { user } = get();
        return user?.user_type === 'INVESTOR';
      },

      isBuyer: () => {
        const { user } = get();
        return user?.user_type === 'BUYER';
      },

      isCompany: () => {
        const { user } = get();
        return user?.user_type === 'COMPANY';
      },

      // 🔥🔥🔥 [2026-01-07 신규] BUSINESS_VISITOR 체크 헬퍼 함수
      isBusinessVisitor: () => {
        const { user } = get();
        return user?.user_type === 'BUSINESS_VISITOR';
      },

      // 🔥🔥🔥 [2026-01-07 신규] 승인 대기 상태 확인 헬퍼 함수
      isPendingApproval: () => {
        const { user } = get();
        if (!user) return false;
        
        // BUYER 또는 BUSINESS_VISITOR이면서 미승인 상태
        if ((user.user_type === 'BUYER' || user.user_type === 'BUSINESS_VISITOR') && !user.is_verified) {
          return true;
        }
        return false;
      },

      // 🔥🔥🔥 [2026-01-07 신규] 승인된 비즈니스 사용자인지 확인
      isApprovedBusinessUser: () => {
        const { user } = get();
        if (!user) return false;
        
        // COMPANY는 항상 승인된 상태
        if (user.user_type === 'COMPANY') return true;
        
        // BUYER, BUSINESS_VISITOR는 is_verified 체크
        if (user.user_type === 'BUYER' || user.user_type === 'BUSINESS_VISITOR') {
          return user.is_verified === true;
        }
        
        return false;
      },

      // 🔥 [기존] 매칭 기능 사용 가능 여부 (BUYER만 가능)
      canUseMatching: () => {
        const { user, isGuest } = get();
        
        // GUEST는 불가
        if (isGuest) return false;
        
        // VISITOR는 불가
        if (user?.user_type === 'VISITOR') return false;
        
        // INVESTOR는 불가 (기본 매칭 알고리즘 제외)
        if (user?.user_type === 'INVESTOR') return false;
        
        // 🔥🔥🔥 [2026-01-07 수정] BUSINESS_VISITOR는 불가 (승인 여부와 무관)
        if (user?.user_type === 'BUSINESS_VISITOR') return false;
        
        // 🔥🔥🔥 [2026-01-07 수정] BUYER는 승인된 경우에만 가능
        if (user?.user_type === 'BUYER') {
          return user.is_verified === true;
        }
        
        // COMPANY는 매칭 기능 사용 가능 여부는 별도 로직에 따름
        // 기본적으로는 BUYER만 가능하므로 false
        return false;
      },

      // 🔥 [기존] 명함 이미지 관련 (INVESTOR 전용)
      hasBusinessCard: () => {
        const { user } = get();
        return !!(user?.business_card_url);
      },

      getBusinessCardUrl: () => {
        const { user } = get();
        return user?.business_card_url || null;
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => ({
        getItem: (name) => {
          try {
            const value = localStorage.getItem(name);
            if (!value) return null;
            
            const parsed = JSON.parse(value);
            
            if (parsed?.state?.user?.id && !isValidUUID(parsed.state.user.id)) {
              logger.warn('⚠️ Storage에서 로드: 유효하지 않은 UUID 감지:', parsed.state.user.id);
              return null;
            }
            
            return value;
          } catch (e) {
            logger.error('❌ Storage 파싱 실패:', e);
            return null;
          }
        },
        setItem: (name, value) => {
          try {
            let stringValue = value;
            
            if (typeof value === 'object') {
              stringValue = JSON.stringify(value);
            } else if (typeof value !== 'string') {
              logger.warn('⚠️ Storage에 저장 시도: 예상치 못한 타입:', typeof value);
              stringValue = String(value);
            }
            
            try {
              const parsed = JSON.parse(stringValue);
              if (parsed?.state?.user?.id && !isValidUUID(parsed.state.user.id)) {
                logger.warn('⚠️ Storage에 저장 시도: 유효하지 않은 UUID, 저장 중단');
                return;
              }
            } catch (e) {
              // 파싱 실패는 무시하고 저장 진행
            }
            
            localStorage.setItem(name, stringValue);
          } catch (error) {
            logger.error('❌ Storage 저장 실패:', error);
          }
        },
        removeItem: (name) => {
          try {
            localStorage.removeItem(name);
          } catch (error) {
            logger.error('❌ Storage 삭제 실패:', error);
          }
        },
      })),
      // 민감한 정보는 persist 제외
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        isGuest: state.isGuest,
        guestExpiresAt: state.guestExpiresAt,
        guestDaysRemaining: state.guestDaysRemaining,
        lastLoginAt: state.lastLoginAt,
        surveyCompleted: state.surveyCompleted, // 🔥 설문 상태도 저장
      }),
    }
  )
);

// 🔥🔥🔥 [2025-12-19 수정] API 인터셉터 설정 제거
// 우리의 apiClient는 axios가 아닌 커스텀 fetch 기반 클라이언트이므로
// apiClient.interceptors가 존재하지 않습니다.
// 토큰 갱신 로직은 apiClient.js 내부에서 처리됩니다.

export { useAuthStore };
export default useAuthStore;
